import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";
import SingleSelect from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import {
  useSaveWebFormFieldMap,
  useWebFormLeadFields,
  webFormErrorDetail
} from "@/entities/webForm/hooks";
import { WebFormFieldMapping, WebFormSettings } from "@/entities/webForm/types";
import {
  MAX_FIELD_MAPPINGS,
  MAX_NAME_LENGTH,
  fieldMapErrors,
  fieldMapUpdate
} from "@/pages/settings/components/integrations/webForm/webFormOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = "integrations.webFormSettings.fieldMap";

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

interface WebFormFieldMapProps {
  settings: WebFormSettings | undefined;
  isLoading: boolean;
  subTitle: string;
  // Another save is running, so this one waits.
  isBusy: boolean;
}

export const WebFormFieldMap: React.FC<WebFormFieldMapProps> = ({
  settings,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const saveFieldMap = useSaveWebFormFieldMap();
  const leadFieldsQuery = useWebFormLeadFields();
  const [saveError, setSaveError] = useState<string | null>(null);

  const [mappings, setMappings] = useState<WebFormFieldMapping[]>(
    settings?.leadFieldMap ?? []
  );
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = settings
    ? JSON.stringify(fieldMapUpdate(settings.leadFieldMap))
    : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (settings) setMappings(settings.leadFieldMap);
  }

  // A saved lead field that a form can no longer fill still shows, by its key,
  // so the admin can see it and pick another. Until the list loads, a saved
  // field shows by its key alone.
  const options = useMemo(() => {
    const fields = (leadFieldsQuery.data ?? []).map((field) => ({
      id: field.key,
      displayName: field.label
    }));
    const known = new Set(fields.map((field) => field.id));
    const missing = [...new Set(mappings.map((mapping) => mapping.leadField))]
      .filter((key) => key && !known.has(key))
      .map((key) => ({
        id: key,
        displayName: leadFieldsQuery.isSuccess
          ? t(`${PREFIX}.unknownField`, { key })
          : key
      }));
    return [...fields, ...missing];
  }, [leadFieldsQuery.data, leadFieldsQuery.isSuccess, mappings, t]);

  const update = fieldMapUpdate(mappings);
  const errors = fieldMapErrors(update);
  const disabled = !settings || isBusy || saveFieldMap.isPending;
  const isDirty = saved !== null && JSON.stringify(update) !== saved;
  const canSave = isDirty && errors.length === 0 && !disabled;

  const editMappings = (
    next: (current: WebFormFieldMapping[]) => WebFormFieldMapping[]
  ) => {
    setMappings(next);
    setSaveError(null);
  };

  const updateMapping = (
    index: number,
    changes: Partial<WebFormFieldMapping>
  ) =>
    editMappings((current) =>
      current.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...changes } : mapping
      )
    );

  const handleSave = () => {
    setSaveError(null);
    saveFieldMap.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-3">
        {leadFieldsQuery.isError && (
          <Text
            variant="smBodyError"
            data-test="web-form-settings-lead-fields-error"
          >
            {t(`${PREFIX}.loadError`)}
          </Text>
        )}
        {mappings.map((mapping, index) => (
          <div
            key={index}
            className="flex items-center gap-2"
            data-test={`web-form-settings-mapping-${index}`}
          >
            <Input
              data-test={`web-form-settings-mapping-form-field-${index}`}
              aria-label={t(`${PREFIX}.a11y.formField`, {
                position: index + 1
              })}
              placeholder={t(`${PREFIX}.formField`)}
              value={mapping.formField}
              onChange={(event) =>
                updateMapping(index, { formField: event.target.value })
              }
              maxLength={MAX_NAME_LENGTH}
              disabled={disabled}
              containerClassName="flex-1"
            />
            <div className="flex-1">
              <SingleSelect
                fieldName={`leadField-${index}`}
                options={options}
                selectedValue={mapping.leadField || undefined}
                onSelection={(value) =>
                  updateMapping(index, { leadField: String(value) })
                }
                formatter={(value) =>
                  options.find((option) => option.id === value)?.displayName ??
                  String(value)
                }
                placeholder={t(`${PREFIX}.leadField`)}
                showSearch
                disabled={disabled}
                isLoading={leadFieldsQuery.isLoading}
                className={SELECT_CLASSNAME}
                popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                variant="form"
              />
            </div>
            <IconButton
              variant="iconNoBg"
              size="lg"
              data-test={`web-form-settings-mapping-remove-${index}`}
              aria-label={t(`${PREFIX}.a11y.removeMapping`, {
                position: index + 1
              })}
              onClick={() =>
                editMappings((current) => current.filter((_, i) => i !== index))
              }
              disabled={disabled}
            >
              <Trash2 />
            </IconButton>
          </div>
        ))}
        <Button
          variant="tertiary"
          className="w-fit"
          data-test="web-form-settings-mapping-add"
          onClick={() =>
            editMappings((current) => [
              ...current,
              { formField: "", leadField: "" }
            ])
          }
          disabled={disabled || mappings.length >= MAX_FIELD_MAPPINGS}
        >
          {t(`${PREFIX}.add`)}
        </Button>
      </div>
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          role="alert"
          data-test="web-form-settings-field-map-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`)}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="web-form-settings-save-field-map"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
